// Sign-in: Firebase Authentication, email link (passwordless), plus Google
// for the Thread team. Firebase only proves "this person controls this email";
// what they may see is decided server-side (src/api/), from Hub Access.
//
// The Firebase web config is public by design, not a secret. See .env.example.
import { initializeApp } from "firebase/app";
import {
  getAuth, onAuthStateChanged, sendSignInLinkToEmail, isSignInWithEmailLink,
  signInWithEmailLink, signInWithPopup, GoogleAuthProvider, signOut as fbSignOut,
} from "firebase/auth";

const config = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
};

let auth = null;
function getHubAuth() {
  if (auth) return auth;
  if (!config.apiKey || !config.projectId) {
    console.error("[Thread Hub] Missing Firebase config. Set REACT_APP_FIREBASE_* (see .env.example).");
    return null;
  }
  auth = getAuth(initializeApp(config));
  return auth;
}

// Where the email address waits between "send me a link" and the click, so
// the click can finish sign-in without asking again (same browser only).
const EMAIL_KEY = "thread-hub:signin-email";
const remember = (email) => { try { localStorage.setItem(EMAIL_KEY, email); } catch (e) {} };
export const rememberedEmail = () => { try { return localStorage.getItem(EMAIL_KEY) || ""; } catch (e) { return ""; } };
const forget = () => { try { localStorage.removeItem(EMAIL_KEY); } catch (e) {} };

// cb(user | null). Returns an unsubscribe function.
export function watchUser(cb) {
  const a = getHubAuth();
  if (!a) { cb(null); return () => {}; }
  return onAuthStateChanged(a, cb);
}

// The link brings them back to the page they asked for.
export async function sendLink(email) {
  const a = getHubAuth();
  if (!a) throw new Error("Sign-in is not configured");
  const url = `${window.location.origin}${window.location.pathname}`;
  await sendSignInLinkToEmail(a, email, { url, handleCodeInApp: true });
  remember(email);
}

export function isEmailLink(href = window.location.href) {
  const a = getHubAuth();
  return !!a && isSignInWithEmailLink(a, href);
}

// Finishes an email-link sign-in, then strips the one-time code from the URL.
export async function completeLink(email, href = window.location.href) {
  const a = getHubAuth();
  await signInWithEmailLink(a, email, href);
  forget();
  window.history.replaceState(null, "", window.location.pathname);
}

export async function signInWithGoogle() {
  const a = getHubAuth();
  if (!a) throw new Error("Sign-in is not configured");
  await signInWithPopup(a, new GoogleAuthProvider());
}

// Firebase refreshes the token itself when it is near expiry; force=true
// fetches a fresh one regardless (used once after a 401).
export async function idToken(force = false) {
  const a = getHubAuth();
  const user = a && a.currentUser;
  return user ? user.getIdToken(force) : null;
}

export async function signOut() {
  const a = getHubAuth();
  if (a) await fbSignOut(a);
}
